import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { ErrorBox, Field, Loading, Success, fmtDate } from '../components/ui';
import { useSettings } from '../hooks';

type ColKey = 'itemNumber' | 'title' | 'price' | 'sellPrice' | 'currency' | 'upc' | 'sourceItemId' | 'category' | 'subcategory' | 'taxCategory';

const FIELDS: { key: ColKey; label: string; hint?: string }[] = [
  { key: 'itemNumber', label: 'Item number (required)' },
  { key: 'title', label: 'Description / title' },
  { key: 'price', label: 'Retail / reference price' },
  { key: 'sellPrice', label: 'Sell price' },
  { key: 'upc', label: 'UPC / barcode', hint: 'Linked to new products so scanning the UPC finds them' },
  { key: 'sourceItemId', label: 'Source system ID' },
  { key: 'category', label: 'Category' },
  { key: 'subcategory', label: 'Subcategory' },
  { key: 'taxCategory', label: 'Tax category' },
  { key: 'currency', label: 'Currency column' },
];

interface Inspect {
  importId?: string;
  detected: { kind: 'csv' | 'xlsx'; encoding: string | null; delimiter: string | null };
  sheets: { name: string; rowCount: number }[];
  headers: string[] | null;
  sampleRows: string[][];
  rowCount: number;
  suggestedColumns: Partial<Record<ColKey, number>>;
  previousImportOfSameFile?: { id: string; filename: string; committedAt: string } | null;
}
interface Summary {
  totalRows: number;
  added: number;
  upgradedFromWeb?: number;
  skippedExisting: number;
  skippedDuplicate: number;
  invalid: number;
  addedWithNotes?: number;
  upcToLink?: number;
}
interface PreviewRow {
  sheet: string;
  rowNumber: number;
  outcome: 'added' | 'upgraded_from_web' | 'skipped_existing' | 'skipped_duplicate_in_file' | 'invalid';
  itemNumber: string | null;
  title: string | null;
  price: string | null;
  sellPrice: string | null;
  currency: string | null;
  upc: string | null;
  linkUpc: boolean;
  messages: string[];
}

const OUTCOME_LABEL: Record<PreviewRow['outcome'], string> = {
  added: 'Will be added',
  upgraded_from_web: 'Fills a web-only item',
  skipped_existing: 'Skipped: already exists',
  skipped_duplicate_in_file: 'Skipped: repeated in file',
  invalid: 'Invalid',
};
const DELIMS: Record<string, string> = { ',': 'Comma', ';': 'Semicolon', '\t': 'Tab', '|': 'Pipe' };
const emptyCols = (): Record<ColKey, string> => Object.fromEntries(FIELDS.map((f) => [f.key, ''])) as Record<ColKey, string>;

export function ProductImportPage() {
  const qc = useQueryClient();
  const settings = useSettings();
  const [file, setFile] = useState<File | null>(null);
  const [info, setInfo] = useState<Inspect | null>(null);
  const [importId, setImportId] = useState<string | null>(null);
  const [opts, setOpts] = useState({ encoding: 'auto', delimiter: 'auto', hasHeaderRow: true, sheet: '*' });
  const [cols, setCols] = useState<Record<ColKey, string>>(emptyCols);
  const [defaultCurrency, setDefaultCurrency] = useState('');
  const [preview, setPreview] = useState<{ summary: Summary; rows: PreviewRow[]; truncated: boolean } | null>(null);
  const [filter, setFilter] = useState<PreviewRow['outcome'] | 'problems' | 'notes'>('problems');
  const [result, setResult] = useState<Summary | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const currency = defaultCurrency || settings.data?.defaultCurrency || 'CAD';

  const applySuggestion = (i: Inspect) => {
    const next = emptyCols();
    for (const f of FIELDS) if (i.suggestedColumns[f.key] !== undefined) next[f.key] = String(i.suggestedColumns[f.key]);
    setCols(next);
  };

  const upload = async () => {
    if (!file || busy) return;
    setBusy('upload');
    setError(null);
    setPreview(null);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await api<Inspect & { importId: string }>('/product-imports', { form, method: 'POST' });
      setImportId(r.importId);
      setInfo(r);
      setOpts({ encoding: 'auto', delimiter: 'auto', hasHeaderRow: true, sheet: '*' });
      applySuggestion(r);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };

  const reinspect = async (next: typeof opts) => {
    setOpts(next);
    setPreview(null);
    setBusy('inspect');
    setError(null);
    try {
      const r = await api<Inspect>(`/product-imports/${importId}/inspect`, { body: next });
      setInfo((prev) => ({ ...r, previousImportOfSameFile: prev?.previousImportOfSameFile }));
      applySuggestion(r);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };

  const options = () => ({
    ...opts,
    columns: Object.fromEntries(FIELDS.map((f) => [f.key, cols[f.key] === '' ? null : Number(cols[f.key])])),
    defaultCurrency: currency,
  });

  const runPreview = async () => {
    if (busy) return;
    setBusy('preview');
    setError(null);
    try {
      setPreview(await api(`/product-imports/${importId}/preview`, { body: options() }));
      setFilter('problems');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };

  const commit = async () => {
    if (busy || !preview) return;
    if (!confirm(`Add ${preview.summary.added} new product(s)? Existing products will not be changed.`)) return;
    setBusy('commit');
    setError(null);
    try {
      const r = await api<{ summary: Summary }>(`/product-imports/${importId}/commit`, { body: options() });
      setResult(r.summary);
      qc.invalidateQueries({ queryKey: ['products'] });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    if (importId && !result) await api(`/product-imports/${importId}/cancel`, { method: 'POST' }).catch(() => undefined);
    setFile(null);
    setInfo(null);
    setImportId(null);
    setPreview(null);
    setResult(null);
    setError(null);
  };

  const colCount = Math.max(info?.headers?.length ?? 0, ...(info?.sampleRows.map((r) => r.length) ?? [0]));
  const colOptions = Array.from({ length: colCount }, (_, i) => ({ v: String(i), label: info?.headers?.[i] ? `${i + 1}: ${info.headers[i]}` : `Column ${i + 1}` }));
  const mapped = new Set(Object.values(cols).filter((v) => v !== ''));
  const setCol = (k: ColKey) => (e: React.ChangeEvent<HTMLSelectElement>) => (setCols({ ...cols, [k]: e.target.value }), setPreview(null));

  const shownRows = useMemo(() => {
    if (!preview) return [];
    const f = (r: PreviewRow) =>
      filter === 'problems' ? r.outcome !== 'added' : filter === 'notes' ? r.outcome === 'added' && r.messages.length > 0 : r.outcome === filter;
    return preview.rows.filter(f).slice(0, 1000);
  }, [preview, filter]);
  const where = (r: { sheet: string; rowNumber: number }) => (r.sheet ? `${r.sheet} ${r.rowNumber}` : String(r.rowNumber));

  if (result) {
    return (
      <div className="narrow">
        <h1>Import complete</h1>
        <Success>
          {result.added} product(s) added{result.upcToLink ? `, ${result.upcToLink} UPC barcode(s) linked` : ''}.{' '}
          {result.upgradedFromWeb ? `${result.upgradedFromWeb} item(s) known only from the Costco website got their master-list details. ` : ''}
          {result.skippedExisting} already existed (left unchanged),{' '}
          {result.skippedDuplicate} repeated in the file, {result.invalid} invalid.
        </Success>
        <div className="row gap wrap">
          <a className="btn" href={`/api/product-imports/${importId}/report.csv`}>
            Download report (problems + notes)
          </a>
          <Link className="btn" to={`/imports/${importId}`}>
            View import record
          </Link>
          <button className="btn btn-primary" onClick={cancel}>
            Import another file
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1>Import products</h1>
      <p className="muted">
        CSV or Excel (.xlsx), e.g. the master item list export. Only new item numbers are added. Existing products are never changed by an import. Blank or "NaN" prices stay blank.
      </p>

      <section className="card">
        <h2>1. Choose file</h2>
        <div className="row gap wrap">
          <input
            type="file"
            accept=".csv,.xlsx,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => (setFile(e.target.files?.[0] ?? null), setInfo(null), setPreview(null))}
            disabled={!!importId}
          />
          {!importId ? (
            <button className="btn btn-primary" disabled={!file || !!busy} onClick={upload}>
              {busy === 'upload' ? 'Reading… (large files take a moment)' : 'Upload'}
            </button>
          ) : (
            <button className="btn btn-ghost" onClick={cancel}>
              Start over
            </button>
          )}
        </div>
        {info?.previousImportOfSameFile && (
          <div className="alert alert-warn">
            This exact file was already imported on {fmtDate(info.previousImportOfSameFile.committedAt)} (
            <Link to={`/imports/${info.previousImportOfSameFile.id}`}>import #{info.previousImportOfSameFile.id}</Link>). Importing again will only add item numbers that do not exist yet.
          </div>
        )}
      </section>

      {info && (
        <section className="card">
          <h2>2. Map columns</h2>
          <div className="grid3">
            {info.detected.kind === 'xlsx' ? (
              <Field label="Sheet" hint={info.sheets.map((s) => `${s.name}: ${s.rowCount} rows`).join(' · ')}>
                <select value={opts.sheet} onChange={(e) => reinspect({ ...opts, sheet: e.target.value })}>
                  <option value="*">All sheets ({info.sheets.length})</option>
                  {info.sheets.map((s) => (
                    <option key={s.name} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </Field>
            ) : (
              <>
                <Field label="Encoding" hint={`Detected: ${info.detected.encoding}`}>
                  <select value={opts.encoding} onChange={(e) => reinspect({ ...opts, encoding: e.target.value })}>
                    <option value="auto">Automatic</option>
                    <option value="utf-8">UTF-8</option>
                    <option value="windows-1252">Windows-1252 (Excel)</option>
                  </select>
                </Field>
                <Field label="Separator" hint={`Detected: ${DELIMS[info.detected.delimiter ?? ','] ?? info.detected.delimiter}`}>
                  <select value={opts.delimiter} onChange={(e) => reinspect({ ...opts, delimiter: e.target.value })}>
                    <option value="auto">Automatic</option>
                    {Object.entries(DELIMS).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </Field>
              </>
            )}
            <Field label="First row">
              <select value={opts.hasHeaderRow ? 'y' : 'n'} onChange={(e) => reinspect({ ...opts, hasHeaderRow: e.target.value === 'y' })}>
                <option value="y">Is a header row</option>
                <option value="n">Is data</option>
              </select>
            </Field>
          </div>
          <p className="muted small">{info.rowCount.toLocaleString()} data rows. Mappings below are suggested from the header names; check them.</p>
          <div className="grid4">
            {FIELDS.map((f) => (
              <Field key={f.key} label={f.label} hint={f.hint}>
                <select value={cols[f.key]} onChange={setCol(f.key)}>
                  <option value="">{f.key === 'itemNumber' ? '— choose —' : '— not imported —'}</option>
                  {colOptions.map((o) => (
                    <option key={o.v} value={o.v}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
            ))}
          </div>
          <Field label="Currency of prices in this file" hint="Used when no currency column is mapped, or a currency cell is blank.">
            <select value={currency} onChange={(e) => (setDefaultCurrency(e.target.value), setPreview(null))}>
              {[...new Set([currency, 'CAD', 'USD'])].map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </Field>
          <div className="table-wrap">
            <table className="table small">
              <thead>
                <tr>
                  {colOptions.map((o) => (
                    <th key={o.v} className={mapped.has(o.v) ? 'mapped' : ''}>
                      {o.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {info.sampleRows.map((r, i) => (
                  <tr key={i}>
                    {colOptions.map((o) => (
                      <td key={o.v} className="pre">
                        {r[Number(o.v)] ?? ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="btn btn-primary mt" disabled={cols.itemNumber === '' || !!busy} onClick={runPreview}>
            {busy === 'preview' ? 'Checking every row…' : 'Preview import'}
          </button>
        </section>
      )}

      {busy === 'inspect' && <Loading label="Re-reading file…" />}
      <ErrorBox error={error} />

      {preview && (
        <section className="card">
          <h2>3. Preview (nothing saved yet)</h2>
          <div className="stats">
            <div className="stat ok">
              <b>{preview.summary.added.toLocaleString()}</b> to add
            </div>
            {!!preview.summary.upgradedFromWeb && (
              <div className="stat ok">
                <b>{preview.summary.upgradedFromWeb.toLocaleString()}</b> web-only items filled in
              </div>
            )}
            <div className="stat">
              <b>{preview.summary.skippedExisting.toLocaleString()}</b> already exist
            </div>
            <div className="stat">
              <b>{preview.summary.skippedDuplicate.toLocaleString()}</b> repeated in file
            </div>
            <div className={`stat ${preview.summary.invalid ? 'bad' : ''}`}>
              <b>{preview.summary.invalid.toLocaleString()}</b> invalid
            </div>
            {preview.summary.addedWithNotes !== undefined && (
              <div className="stat">
                <b>{preview.summary.addedWithNotes.toLocaleString()}</b> added with notes
              </div>
            )}
            {preview.summary.upcToLink !== undefined && cols.upc !== '' && (
              <div className="stat">
                <b>{preview.summary.upcToLink.toLocaleString()}</b> UPCs to link
              </div>
            )}
          </div>
          <div className="row gap wrap">
            <select value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)} aria-label="Show rows">
              <option value="problems">Rows not being added</option>
              <option value="notes">Added, with notes</option>
              {Object.entries(OUTCOME_LABEL).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
            <a className="btn btn-small" href={`/api/product-imports/${importId}/report.csv`}>
              Download report (problems + notes)
            </a>
            <a className="btn btn-small" href={`/api/product-imports/${importId}/report.csv?all=1`}>
              Download all rows
            </a>
          </div>
          {preview.truncated && <p className="muted small">The screen shows the first rows of each kind. The downloadable report has every row.</p>}
          <div className="table-wrap">
            <table className="table small">
              <thead>
                <tr>
                  <th>{info?.detected.kind === 'xlsx' ? 'Sheet / row' : 'File row'}</th>
                  <th>Result</th>
                  <th>Item #</th>
                  <th>Title</th>
                  <th>Retail</th>
                  <th>Sell</th>
                  <th>UPC</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {shownRows.map((r) => (
                  <tr key={`${r.sheet}-${r.rowNumber}`} className={`outcome-${r.outcome}`}>
                    <td className="nowrap">{where(r)}</td>
                    <td>{OUTCOME_LABEL[r.outcome]}</td>
                    <td className="mono pre">{r.itemNumber ?? <span className="missing">missing</span>}</td>
                    <td>{r.title ?? <span className="missing">blank</span>}</td>
                    <td>{r.price ? `${r.price} ${r.currency}` : <span className="missing">blank</span>}</td>
                    <td>{r.sellPrice ? `${r.sellPrice} ${r.currency}` : <span className="missing">blank</span>}</td>
                    <td className="mono">{r.upc ? `${r.upc}${r.linkUpc ? '' : ' (not linked)'}` : ''}</td>
                    <td className="small">{r.messages.join('; ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {shownRows.length === 0 && <p className="muted">No rows in this view.</p>}
          </div>
          <div className="row gap mt">
            <button className="btn btn-primary btn-big" disabled={!!busy || preview.summary.added === 0} onClick={commit}>
              {busy === 'commit' ? 'Importing… (this can take a minute)' : `Add ${preview.summary.added.toLocaleString()} product(s)`}
            </button>
            <button className="btn btn-ghost" onClick={cancel} disabled={!!busy}>
              Cancel import
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
